import React from "react";

// Third-party
import PropTypes from "prop-types";
import * as Sentry from "@sentry/react";
import { withTranslation } from "react-i18next";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const { userName, version } = this.props;

    // Log error to Sentry and capture the event ID
    Sentry.withScope(scope => {
      // Add component stack context
      scope.setContext("errorBoundary", {
        componentStack: errorInfo.componentStack
      });

      // Add user information if available
      if (userName) {
        scope.setUser({ username: userName });
      }

      // Add app version if available
      if (version) {
        scope.setTag("app_version", version);
        scope.setContext("app", {
          version: version
        });
      }

      const eventId = Sentry.captureException(error);

      // Store error details and event ID in state for display
      this.setState({
        error,
        errorInfo,
        eventId
      });
    });

    // Also log to console for development
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReload = async () => {
    // Flush Sentry events before reloading
    // to ensure the error is sent to the server
    await Sentry.flush(2000);
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI to prevent blank screen
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            width: "100vw",
            backgroundColor: "#0F1B34",
            padding: "10px",
            fontFamily: "Arial, sans-serif"
          }}
        >
          <div
            style={{
              maxWidth: "400px",
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              textAlign: "center"
            }}
          >
            {/* Logo and App Name */}
            <div style={{ marginBottom: "10px" }}>
              <img
                src="/gilytics_error_logo.svg"
                alt="Pathfinder"
                style={{ width: "70px", height: "auto" }}
              />
            </div>

            <h1
              style={{
                color: "#d32f2f",
                marginBottom: "10px",
                fontSize: "24px"
              }}
            >
              {this.props.t("errorBoundary.title")}
            </h1>

            {/* Sentry Event ID Reference */}
            {this.state.eventId && (
              <div
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: "12px",
                  borderRadius: "4px",
                  marginBottom: "20px",
                  fontSize: "13px",
                  color: "#666"
                }}
              >
                <strong>{this.props.t("errorBoundary.referenceId")}</strong>
                <br />
                <code
                  style={{
                    backgroundColor: "#e0e0e0",
                    padding: "4px 8px",
                    borderRadius: "3px",
                    fontSize: "12px",
                    fontFamily: "monospace",
                    display: "inline-block",
                    marginTop: "5px"
                  }}
                >
                  {this.state.eventId}
                </code>
                <br />
                <span
                  style={{
                    fontSize: "11px",
                    color: "#999",
                    marginTop: "5px",
                    display: "block"
                  }}
                >
                  {this.props.t("errorBoundary.contactSupport")}
                </span>
              </div>
            )}

            <button
              onClick={this.handleReload}
              style={{
                padding: "12px 24px",
                backgroundColor: "#1976d2",
                color: "white",
                border: "none",
                borderRadius: "4px",
                fontSize: "16px",
                cursor: "pointer",
                fontWeight: "500"
              }}
              onMouseOver={e => (e.target.style.backgroundColor = "#1565c0")}
              onMouseOut={e => (e.target.style.backgroundColor = "#1976d2")}
            >
              {this.props.t("errorBoundary.reloadButton")}
            </button>

            {/* App Version */}
            {this.props.version && (
              <div
                style={{
                  marginTop: "20px",
                  fontSize: "12px",
                  color: "#999"
                }}
              >
                {this.props.t("errorBoundary.version", {
                  version: this.props.version
                })}
              </div>
            )}

            {/* {process.env.NODE_ENV === "development" && this.state.error && (
              <details
                style={{
                  marginTop: "30px",
                  textAlign: "left",
                  backgroundColor: "#f5f5f5",
                  padding: "15px",
                  borderRadius: "4px",
                  cursor: "pointer"
                }}
              >
                <summary style={{ fontWeight: "bold", marginBottom: "10px" }}>
                  {this.props.t("errorBoundary.errorDetails")}
                </summary>
                <pre
                  style={{
                    fontSize: "12px",
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word"
                  }}
                >
                  {this.state.error.toString()}
                  {"\n\n"}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )} */}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  userName: PropTypes.string,
  version: PropTypes.string,
  t: PropTypes.func.isRequired
};

export default withTranslation()(ErrorBoundary);
